import {
  ArrowRight,
  DollarSign,
  Calendar,
  TrendingDown,
  Clock,
  FileText,
  Calculator,
} from "lucide-react";
import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import CredibilityStrip from "@/components/CredibilityStrip";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import HeroCarousel from "@/components/HeroCarousel";
import OrbitalMesh from "@/components/OrbitalMesh";
import TrustLayerStack from "@/components/TrustLayerStack";
import MeshlyLifecycle from "@/components/MeshlyLifecycle";
import ConcreteHeroContent from "@/components/ConcreteHeroContent";
import ConcreteHeroVisual from "@/components/ConcreteHeroVisual";
import Header from "@/components/Header";
import { SEOHead } from "@/components/SEOHead";
import { useDemoModal } from "@/context/DemoModalContext";
import { useExperiment } from "@/hooks/useExperiment";

const TestimonialsCarousel = lazy(
  () => import("@/components/TestimonialsCarousel"),
);

// FAQPage schema, emitted as JSON-LD in the homepage head. AI search tools
// extract Q&A pairs verbatim, so this is our highest-leverage on-page surface
// for (a) stating the new "trust/verification layer" positioning and (b) the
// explicit Meshly-vs-Mesh disambiguation. Answers mirror the on-page FAQ copy
// (currently behind a `false &&` feature flag) so re-enabling that section keeps
// the schema and visible content in sync.
const HOME_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is Meshly the same as Mesh?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. Meshly (Meshly Labs, Inc., based in Palo Alto, California, founded by Sandra Bustos) is the trust harness for the systems your business runs on: it watches them, catches when they disagree, and proves what your people and your agents actually did, starting with revenue. \"Mesh\" is a separate, unrelated company. Different company, different product.",
      },
    },
    {
      "@type": "Question",
      name: "What is Meshly?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Meshly is the AI verification layer for the systems your business runs on. It continuously understands business meaning, detects drift between systems of record (CRM, contracts, billing, ERP, and the general ledger), and enables explainable, traceable decisions before downstream systems or agents take action.",
      },
    },
    {
      "@type": "Question",
      name: "What is system drift and why does it matter?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "System drift is when the systems your business runs on stop agreeing about the same thing: the contract says one number, billing reflects another, the ledger a third. When the systems disagree, revenue leaks, the close slips, and the audit gets harder. Finance teams lose days every month reconciling systems that were never designed to agree.",
      },
    },
    {
      "@type": "Question",
      name: "What makes Meshly different from other AI tools?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most AI tools check whether the answer is right. Meshly checks whether anyone can stand behind what was done. Whether a person, an automation, or an agent moved a number, Meshly verifies it against the source and proves what happened, so any number traces back to who changed it and where it came from. As AI moves into the back office, that verification is the difference between trusting the output and being able to prove it.",
      },
    },
    {
      "@type": "Question",
      name: "Is Meshly an integration platform?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. Integrations move your data between systems. Meshly verifies that the meaning survived the trip. It is the layer above your integrations, not another one. It is not a CRM, a billing system, or an ERP, and not an OCR tool.",
      },
    },
  ],
};

const HERO_STATS = [
  { value: "4 days", label: "saved per month" },
  { value: "1–5%", label: "EBITDA recovered" },
  { value: "Day 1", label: "no migration needed" },
];

const Index = () => {
  const { openModal } = useDemoModal();
  // A/B test: abstract rotating hero copy (control) vs. one concrete,
  // specific catch (variant). Resolves to null until the client picks a
  // bucket, so SSG output and first paint always show the control hero.
  const heroVariant = useExperiment("hero_concrete_vs_carousel");
  const isConcreteHero = heroVariant === "variant";

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Meshly: the trust harness for the systems your business runs on"
        description="Meshly is the AI verification layer for the systems your business runs on. It watches them, catches when they go out of sorts, and proves what your people and your agents actually did. We start at revenue."
        canonicalUrl="https://www.meshly.ai/"
        structuredData={HOME_FAQ_SCHEMA}
      />
      <Header />
      {/* Hero Section */}
      <section className="relative pt-6 pb-12 lg:pt-14 lg:pb-32 px-4 overflow-hidden bg-background">
        {/* instrument backdrop now lives inside OrbitalMesh as a concentric halo,
            so every ring shares the Meshly core's center */}

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="max-w-[720px]">
              {isConcreteHero ? <ConcreteHeroContent /> : <HeroCarousel />}

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mt-8 lg:mt-10">
                {isConcreteHero ? (
                  <Button asChild size="lg" className="group rounded-none px-7 w-full sm:w-auto">
                    <Link to="/roi-calculator">
                      Run your numbers
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                ) : (
                  <Button size="lg" className="group rounded-none px-7 w-full sm:w-auto" onClick={openModal}>
                    Book a working session
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                )}
                <a
                  href="#how-it-works"
                  className="font-mono-tech text-sm text-foreground hover:text-primary transition-colors"
                >
                  See how it works &rarr;
                </a>
              </div>
            </div>

            {/* Hero Visual - the instrument (control) or a real catch (variant) */}
            <div className="relative flex items-center justify-center">
              {isConcreteHero ? <ConcreteHeroVisual /> : <OrbitalMesh />}
            </div>
          </div>

          {isConcreteHero && (
            <div className="mt-12 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-3 sm:max-w-2xl">
              {HERO_STATS.map((stat) => (
                <div key={stat.label} className="bg-background p-6">
                  <p className="font-mono-tech text-2xl font-semibold text-foreground">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section separator */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
      </section>

      <CredibilityStrip />

      {/* 1 · The problem */}
      <section id="why" className="border-t border-border bg-secondary/40 py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl">
            <span className="mb-4 inline-block font-mono-tech text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              The problem
            </span>
            <h2 className="max-w-3xl font-display text-4xl md:text-5xl font-semibold tracking-[-0.02em] text-foreground">
              Your systems never agreed.
            </h2>
            <p className="mt-8 max-w-3xl border-l-2 border-primary pl-6 text-xl leading-relaxed text-foreground">
              Mismatched fields trigger manual cleanup, delayed close, and
              downstream rework. Finance teams lose at minimum{" "}
              <span className="font-semibold text-primary">four days every month</span>,
              nearly <span className="font-semibold text-primary">20 percent of the year</span>,
              reconciling systems that were never designed to agree.
            </p>
            <div className="mt-12 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-background p-6">
                <DollarSign className="mb-3 h-5 w-5 text-primary" />
                <p className="text-sm font-semibold text-foreground">Revenue leakage</p>
                <p className="mt-1 text-sm leading-snug text-muted-foreground"><span className="font-mono-tech font-semibold text-primary">42%</span> of companies report it</p>
              </div>
              <div className="bg-background p-6">
                <Calendar className="mb-3 h-5 w-5 text-primary" />
                <p className="text-sm font-semibold text-foreground">Delayed close</p>
                <p className="mt-1 text-sm leading-snug text-muted-foreground"><span className="font-mono-tech font-semibold text-primary">~6 days</span> to close each month</p>
              </div>
              <div className="bg-background p-6">
                <TrendingDown className="mb-3 h-5 w-5 text-primary" />
                <p className="text-sm font-semibold text-foreground">EBITDA drag</p>
                <p className="mt-1 text-sm leading-snug text-muted-foreground">order-to-cash leaks <span className="font-mono-tech font-semibold text-primary">1 to 5%</span> of EBITDA</p>
              </div>
              <div className="bg-background p-6">
                <Clock className="mb-3 h-5 w-5 text-primary" />
                <p className="text-sm font-semibold text-foreground">Manual rework</p>
                <p className="mt-1 text-sm leading-snug text-muted-foreground"><span className="font-mono-tech font-semibold text-primary">15 to 20 hrs</span> a week on manual rev rec</p>
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-6">
              <Link to="/report/cost-of-fragmentation" className="inline-flex items-center gap-2 font-mono-tech text-sm text-primary underline underline-offset-4 transition-colors hover:text-primary/80">
                <FileText className="h-4 w-4" />
                Read the report on the cost of fragmentation
              </Link>
              <Link to="/roi-calculator" className="inline-flex items-center gap-2 font-mono-tech text-sm text-primary underline underline-offset-4 transition-colors hover:text-primary/80">
                <Calculator className="h-4 w-4" />
                See how much this is costing you
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · What is Meshly? — positioning / category */}
      <section className="border-t border-border bg-background py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl">
            <span className="mb-4 inline-block font-mono-tech text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              Where it fits
            </span>
            <h2 className="max-w-3xl font-display text-4xl md:text-5xl font-semibold tracking-[-0.02em] text-foreground">
              What is Meshly?
            </h2>
            <p className="mb-12 mt-6 max-w-2xl text-lg leading-relaxed text-foreground/75">
              Meshly is the trust harness for the systems your business runs on. It
              continuously understands business meaning, detects drift, and enables
              explainable, traceable decisions before downstream systems or agents
              take action.
            </p>
            <TrustLayerStack />
          </div>
        </div>
      </section>

      {/* 2b · Core product pillars (from the Sep 2026 CFO deck, slide 4) */}
      <section className="border-t border-border bg-secondary/40 py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl">
            <span className="mb-4 inline-block font-mono-tech text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              The trust harness
            </span>
            <h2 className="max-w-3xl font-display text-4xl md:text-5xl font-semibold tracking-[-0.02em] text-foreground">
              Core product pillars
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {[
                {
                  n: "01",
                  name: "Governance",
                  body: "Makes how a number became the answer visible, controllable, and provable.",
                },
                {
                  n: "02",
                  name: "Finance infrastructure",
                  body: "Makes business meaning legible across systems, without requiring the systems to line up first.",
                },
                {
                  n: "03",
                  name: "Extensibility",
                  body: "Makes trusted business meaning available to the tools, workflows, and agents your team already uses.",
                },
              ].map((p) => (
                <div key={p.n} className="rounded-lg border border-border bg-background p-6">
                  <span className="font-mono-tech text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                    {p.n} / Pillar
                  </span>
                  <h3 className="mt-3 font-display text-xl font-semibold uppercase tracking-wide text-foreground">
                    {p.name}
                  </h3>
                  <p className="mt-3 leading-relaxed text-foreground/75">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3 · How Meshly works — product / mechanics (distinct chapter) */}
      <section id="how-it-works" className="scroll-mt-20 border-t border-border bg-background py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl">
            <span className="mb-4 inline-block font-mono-tech text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              The product
            </span>
            <h2 className="max-w-3xl font-display text-4xl md:text-5xl font-semibold tracking-[-0.02em] text-foreground">
              How Meshly works
            </h2>
            <p className="mb-12 mt-6 max-w-2xl text-lg leading-relaxed text-foreground/75">
              Follow one agreement through it. Meshly understands its meaning,
              compares it across your systems, surfaces the one place they
              disagree, explains it in full, and lets a person or an agent
              resolve it, audit-ready.
            </p>
            <MeshlyLifecycle />
          </div>
        </div>
      </section>

      {/* HIDDEN for now — restore by deleting the `false && (<>` wrapper and its `</>)}` below */}
      {false && (
        <>
      {/* Customer Testimonials - lazy loaded */}
      <Suspense fallback={null}>
        <TestimonialsCarousel />
      </Suspense>

      {/* FAQ Section */}
      <section className="py-24 bg-background border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <span className="mb-4 inline-block font-mono-tech text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                Questions
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-[-0.02em] text-foreground">
                Frequently asked
              </h2>
            </div>

            <Accordion type="single" collapsible className="border-t border-border">
              {/* FAQ 1 - Understanding System Drift */}
              <AccordionItem
                value="item-1"
                className="border-b border-border"
              >
                <AccordionTrigger className="px-1 py-5 text-left hover:no-underline transition-colors data-[state=open]:text-primary">
                  <span className="text-lg font-bold text-foreground text-left">
                    What is system drift and why does it matter?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-1 pb-6">
                  <p className="text-muted-foreground leading-relaxed">
                    System drift is when the systems your business runs on stop
                    agreeing about the same thing. The contract says one number,
                    billing reflects another, the ledger a third. It happens
                    through manual updates, integration gaps, and changes made
                    after a deal closes, and now through the AI and agents acting
                    on those systems faster than anyone can check. When the
                    systems disagree, revenue leaks, the close slips, and the
                    audit gets harder. Finance teams lose days every month
                    reconciling systems that were never designed to agree.
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* FAQ 2 - How Meshly Works */}
              <AccordionItem
                value="item-2"
                className="border-b border-border"
              >
                <AccordionTrigger className="px-1 py-5 text-left hover:no-underline transition-colors data-[state=open]:text-primary">
                  <span className="text-lg font-bold text-foreground text-left">
                    How does Meshly work?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-1 pb-6">
                  <p className="text-muted-foreground leading-relaxed">
                    Meshly is the trust harness across the systems your business
                    runs on. It continuously watches them and catches when they
                    go out of sorts: a renamed field, a shifted amount, a wrong
                    term, a missing signature. It does this without a predefined
                    schema, learning the structure of your data and tracking it
                    as it changes, so it catches the drift instead of waiting for
                    you to write rules. Meshly then runs the back-office work
                    that follows and produces an artifact a person approves. It
                    never changes your numbers on its own. The machine does the
                    watching. Your people do the judgment.
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* FAQ 3 - Why Choose Meshly */}
              <AccordionItem
                value="item-3"
                className="border-b border-border"
              >
                <AccordionTrigger className="px-1 py-5 text-left hover:no-underline transition-colors data-[state=open]:text-primary">
                  <span className="text-lg font-bold text-foreground text-left">
                    Why do revenue teams choose Meshly?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-1 pb-6">
                  <p className="text-muted-foreground leading-relaxed">
                    Because Meshly solves the problem no other tool addresses:
                    the gap between systems. CPQ tools help sales configure deals
                    but don't validate what happens downstream. Revenue
                    recognition platforms handle the accounting rules but trust
                    the data they receive. Meshly fills the layer in between,
                    verifying that the contract, the billing schedule, and the
                    recognized revenue still agree, and proving it with a trail
                    back to the source. Teams report a faster close, fewer
                    revenue adjustments, and far less time on manual cleanup. We
                    start at revenue because that is where the leak is sharpest
                    and the dollars are countable.
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* FAQ 4 - Revenue Intelligence Difference */}
              <AccordionItem
                value="item-4"
                className="border-b border-border"
              >
                <AccordionTrigger className="px-1 py-5 text-left hover:no-underline transition-colors data-[state=open]:text-primary">
                  <span className="text-lg font-bold text-foreground text-left">
                    What makes Meshly different from other AI tools?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-1 pb-6">
                  <p className="text-muted-foreground leading-relaxed">
                    Most AI tools check whether the answer is right. Meshly
                    checks whether anyone can stand behind what was done. Whether
                    a person, an automation, or an agent moved a number, Meshly
                    verifies it against the source and proves what happened, so
                    any number traces back to who changed it and where it came
                    from, in one click. It is system-aware and
                    cross-functional, so it catches the edge
                    cases that break at the handoffs between sales, finance, and
                    operations, like mid-term amendments or pro-ration errors,
                    and explains them before they compound. As AI moves into the
                    back office, that verification is the difference between
                    trusting the output and being able to prove it.
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* FAQ 5 - What Meshly is not */}
              <AccordionItem
                value="item-5"
                className="border-b border-border"
              >
                <AccordionTrigger className="px-1 py-5 text-left hover:no-underline transition-colors data-[state=open]:text-primary">
                  <span className="text-lg font-bold text-foreground text-left">
                    Is Meshly an integration platform?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-1 pb-6">
                  <p className="text-muted-foreground leading-relaxed">
                    No. Integrations move your data between systems. Meshly
                    verifies that the meaning survived the trip. It is the layer
                    above your integrations, not another one. It is also not a
                    CRM, a billing system, or an ERP, and not an OCR tool that
                    reads one document well. Meshly catches when the next
                    document does not match everything before it, and makes the
                    systems you already have trustworthy.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>
        </>
      )}

      {/* Global footer now mounted in RootLayout — single source of truth */}
    </div>
  );
};

export default Index;
